import React from 'react';
import { Card, CardContent, CardHeader, CardActions, Typography, IconButton, Box, Divider } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { motion } from 'framer-motion';

/**
 * A customized card component for the dashboard
 * 
 * @param {string} title - Card title
 * @param {string} subtitle - Card subtitle
 * @param {node} headerAction - Action component to display in header
 * @param {boolean} showMoreOptions - Whether to show more options button in header
 * @param {function} onMoreOptionsClick - Click handler for more options button
 * @param {boolean} elevation - Card elevation (0-24)
 * @param {object} sx - Additional styles for the card
 * @param {string} animation - Animation type (hover, scale, none)
 * @param {node} footer - Footer content
 * @param {boolean} divider - Whether to show divider between header and content
 * @param {boolean} footerDivider - Whether to show divider between content and footer
 */
const DashboardCard = ({
  children,
  title,
  subtitle,
  headerAction,
  showMoreOptions = false,
  onMoreOptionsClick,
  elevation = 1,
  sx = {},
  animation = 'hover',
  footer,
  divider = false,
  footerDivider = true,
}) => {
  // Prepare motion variants
  const motionVariants = {
    hover: {
      initial: {},
      whileHover: { y: -5, boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)' },
    },
    scale: {
      initial: {},
      whileHover: { scale: 1.02, boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)' },
    },
    none: {
      initial: {},
      whileHover: {},
    },
  };

  // Get animation settings
  const animationSettings = motionVariants[animation] || motionVariants.none;

  const MotionDiv = motion.div;


  return (
    <MotionDiv
      initial={animationSettings.initial}
      whileHover={animationSettings.whileHover}
      transition={{ duration: 0.3 }}
    >
      <Card
        elevation={elevation}
        sx={{
          height: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          ...sx,
        }}
      >
        {title && (
          <CardHeader
            title={
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            }
            subheader={subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
            action={
              <>
                {headerAction}
                {showMoreOptions && (
                  <IconButton onClick={onMoreOptionsClick} size="small">
                    <MoreVert fontSize="small" />
                  </IconButton>
                )}
              </>
            }
          />
        )}
        
        {divider && <Divider />}
        
        <CardContent sx={{ flexGrow: 1, padding: (theme) => theme.spacing(3) }}>
          {children}
        </CardContent>
        
        {footer && (
          <>
            {footerDivider && <Divider />}
            <CardActions sx={{ padding: (theme) => theme.spacing(2) }}>
              {footer}
            </CardActions>
          </>
        )}
      </Card>
    </MotionDiv>
  );
};

export default DashboardCard;